import { makeAutoObservable, runInAction } from "mobx";
import { OsaNodeCoding } from "../../coding/models/OsaNodeCoding";
import {
  isOsaWayVisibleThroughFilters,
  isNodeVisibleByAttributes,
  isNodeVisibleByScore,
  isNodeVisibleByTreatment,
  isLifeCycleStateVisibleThroughFilters,
  isOsaWayVisibleThroughAssessmentTypeFilters,
  isHlaWayVisibleThroughAssessmentTypeFilters,
} from "../utils/apply-filters";
import { Score, TreatmentType } from "@keita/lanepatrol-sdk";
import {
  AssessmentType,
  AttributeFilter,
  AttributeSorting,
  ScoreFilters,
} from "../../map/models/MapFilters";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNode, Treatment } from "../../osaway/models/OsaNode";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import { OsaWay } from "../../osaway/models/OsaWay";
import { HlaWay } from "../../ra/models/HlaWay";

type osaWayId = string;

export class FilterStore {
  rootStore;

  assessmentTypeFilters: AssessmentType | null = null;
  osaWayFilters: osaWayId[] = [];
  lifeCycleStageFilters: LifeCycleStage[] = [];
  scoreFilters: ScoreFilters = {};
  treatmentFilters: TreatmentType[] = [];
  attributeFilters: AttributeFilter[] = [];
  attributeSorting = AttributeSorting.Alphabetic;

  filteredHlaWays: HlaWay[] = [];
  filteredOsaWays: OsaWay[] = [];
  filteredNodes: OsaNode[] = [];
  filteredDistance = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.resetFilters();
    runInAction(() => {
      this.attributeFilters = [];
      this.attributeSorting = AttributeSorting.Alphabetic;
      this.filteredOsaWays = [];
      this.filteredNodes = [];
      this.filteredHlaWays = [];
      this.filteredDistance = 0;
    });
  }

  resetFilters() {
    runInAction(() => {
      this.assessmentTypeFilters = null;
      this.osaWayFilters = [];
      this.lifeCycleStageFilters = [];
      this.scoreFilters = {};
      this.treatmentFilters = [];
    });
    if (this.rootStore.forecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(this.lifeCycleStageFilters);
    }
  }

  get originalOrForecastedOsaWays(): OsaWay[] {
    const { forecastStore } = this.rootStore;
    return forecastStore.computedValues?.osaWays ?? this.osaWaysAvailableToUser;
  }

  get originalOrForecastedNodes(): Map<string, OsaNode[]> {
    const { forecastStore } = this.rootStore;
    return (
      forecastStore.computedValues?.osaNodes ?? this.nodesAvailableInCurrentMode
    );
  }

  get originalOrForecastedAllNodeCodingsByWayId(): Map<
    string,
    Map<string, OsaNodeCoding>
  > {
    const { forecastStore, codingStore } = this.rootStore;
    return (
      forecastStore.computedValues?.allNodeCodingsByWayId ??
      codingStore.allNodeCodingsByWayId
    );
  }

  get nodesAvailableInCurrentMode(): Map<string, OsaNode[]> {
    const { osaNodeStore } = this.rootStore;
    const availableNodes = new Map<string, OsaNode[]>();
    for (const way of this.osaWaysAvailableToUser) {
      const nodes = osaNodeStore.nodesByWayId.get(way.id);
      if (nodes) {
        availableNodes.set(way.id, nodes);
      }
    }

    return availableNodes;
  }

  get osaWaysAvailableToUser(): OsaWay[] {
    const { osaWayStore } = this.rootStore;

    return osaWayStore.ways.filter((way) => this.isOsaWayAvailableToUser(way));
  }

  filterHlaWays() {
    const { hlaStore } = this.rootStore;

    runInAction(() => {
      this.filteredHlaWays = hlaStore.ways.filter((way) =>
        this.isHlaWayVisible(way),
      );
    });

    hlaStore.updateMapSource();
    this.calculateFilteredDistance();
  }

  filterOsaWays() {
    runInAction(() => {
      this.filteredOsaWays = this.originalOrForecastedOsaWays.filter((way) =>
        this.isOsaWayVisible(way),
      );
    });
    this.filterNodes();
  }

  filterNodes() {
    const allNodes = this.originalOrForecastedNodes;
    const filteredNodes: OsaNode[] = [];

    this.filteredOsaWays.forEach((way) => {
      const nodes = allNodes.get(way.id) ?? [];
      filteredNodes.push(
        ...nodes.filter(
          (s) =>
            this.isNodeVisibleByScore(s.score) &&
            this.isNodeVisibleByAttributes(way.id, s.id!) &&
            this.isNodeVisibleByTreatment(s.treatments),
        ),
      );
    });

    runInAction(() => {
      this.filteredNodes = filteredNodes;
    });

    this.calculateFilteredDistance();
  }

  calculateFilteredDistance() {
    runInAction(() => {
      this.filteredDistance =
        this.filteredNodes.reduce((acc, node) => acc + node.distanceToNext, 0) +
        this.filteredHlaWays.reduce((acc, hlaWay) => acc + hlaWay.lengthM, 0);
    });
  }

  setAttributeSorting(value: AttributeSorting) {
    runInAction(() => {
      this.attributeSorting = value;
    });
  }

isHlaWayVisible(hlaWay: HlaWay) {
  if (this.attributeFilters.length) return false;
  if (Object.keys(this.scoreFilters).length) return false;
  if (this.treatmentFilters.length) return false;
  if (this.lifeCycleStageFilters.length) return false;
  return isHlaWayVisibleThroughAssessmentTypeFilters(this.assessmentTypeFilters);
}

  isOsaWayAvailableToUser(way: OsaWay) {
    const { authStore } = this.rootStore;
    const hasCompletedOsaWay = way.lifeCycleStage === LifeCycleStage.Completed;
    return authStore.isAuthenticated || hasCompletedOsaWay;
  }

  isOsaWayVisible(way: OsaWay) {
    return (
      this.osaWaysAvailableToUser &&
      isOsaWayVisibleThroughAssessmentTypeFilters(this.assessmentTypeFilters) &&
      isLifeCycleStateVisibleThroughFilters(way, this.lifeCycleStageFilters) &&
      isOsaWayVisibleThroughFilters(way.id, this.osaWayFilters)
    );
  }

  isNodeVisibleByAttributes(osaWayId: string, nodeId: string) {
    if (!this.attributeFilters.length) return true;

    const allNodeCodingsByWayId =
      this.originalOrForecastedAllNodeCodingsByWayId;
    const codingEntry = allNodeCodingsByWayId.get(osaWayId)?.get(nodeId);
    if (!codingEntry) return true;

    return isNodeVisibleByAttributes(
      codingEntry.attributes,
      this.attributeFilters,
    );
  }

  isNodeVisibleByScore(score: Score | null) {
    return isNodeVisibleByScore(score, this.scoreFilters);
  }

  isNodeVisibleByTreatment(nodeTreatments: Treatment[] | null) {
    return isNodeVisibleByTreatment(nodeTreatments, this.treatmentFilters);
  }

  setAttributeFilters(value: AttributeFilter[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.attributeFilters = value;
    });
    this.filterNodes();
    this.filterHlaWays()

    mapStore.recenterMap();
  }
  setScoreFilters(value: ScoreFilters) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.scoreFilters = value;
    });
    this.filterNodes();
    this.filterHlaWays()
    mapStore.recenterMap();
  }
  setAssessmentTypeFilters(value: AssessmentType | null) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.assessmentTypeFilters = value;
    });
    this.filterHlaWays();
    this.filterOsaWays();
    mapStore.recenterMap();
  }
  setOsaWayFilters(value: string[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.osaWayFilters = value;
    });
    this.filterOsaWays();
    this.filterHlaWays()
    mapStore.recenterMap();
  }
  setLifeCycleStageFilters(value: LifeCycleStage[]) {
    const { mapStore } = this.rootStore;
    if (this.rootStore.forecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(value);
    } else {
      runInAction(() => {
        this.lifeCycleStageFilters = value;
      });
      this.filterOsaWays();
      this.filterHlaWays()
      mapStore.recenterMap();
    }
  }
  forceCodedLifeCycleStageFilters(value: LifeCycleStage[]) {
    runInAction(() => {
      // force selection of Review or Completed
      const codedStages = new Set(value);
      codedStages.delete(LifeCycleStage.Design);
      codedStages.delete(LifeCycleStage.DataCollection);
      codedStages.delete(LifeCycleStage.Coding);
      if (codedStages.size === 0) {
        codedStages.add(LifeCycleStage.Review);
        codedStages.add(LifeCycleStage.Completed);
      }
      this.lifeCycleStageFilters = [...codedStages];
    });
    this.filterOsaWays();
    this.filterHlaWays()
  }
  setTreatmentFilters(value: TreatmentType[]) {
    runInAction(() => {
      this.treatmentFilters = value;
    });
    this.filterNodes();
    this.filterHlaWays()
  }
}
